import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@shared/ui/Button'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { useAppStore } from '@/store/useAppStore'
import {
  useSetAutoRenewMutation,
  useCancelReasonMutation,
  useSubscriptionPricesQuery,
  usePlanLookup,
  formatPrice,
  CancelAutoRenewConfirmModal,
  AutoRenewDisabledResultModal,
} from '@features/subscription'
import {
  ProviderPickerModal,
  useBuySubscriptionMutation,
  usePaymentMethodsQuery,
  useStartPayment,
  type ProviderPickerSubmit,
} from '@features/payments'
import RecommendedChannels from '../RecommendedChannels'
import { PaymentMethodsBlock } from './PaymentMethodsBlock'
import styles from './SubscriptionSection.module.css'

interface SubscriptionSectionProps {
  onSettingsClick: () => void
}

interface ActiveDateRowProps {
  formattedExpiresAt: string | null
  autoRenew: boolean
  showToggle: boolean
  toggleDisabled: boolean
  onToggle: () => void
  bonusDaysLabel?: string | null
}

const ActiveDateRow = ({
  formattedExpiresAt,
  autoRenew,
  showToggle,
  toggleDisabled,
  onToggle,
  bonusDaysLabel,
}: ActiveDateRowProps) => (
  <div className={styles.activeRow}>
    <div className={styles.activeColumn}>
      <span className={styles.activeLabel}>Активна до:</span>
      <span className={styles.activeDate}>
        {formattedExpiresAt ?? '—'}
        {bonusDaysLabel && (
          <span className={styles.bonusDays}> · {bonusDaysLabel}</span>
        )}
      </span>
    </div>
    {showToggle && (
      <div className={styles.activeColumn}>
        <span className={styles.activeLabel}>Автопродление</span>
        <button
          type="button"
          className={`${styles.autoRenewToggle} ${autoRenew ? styles.autoRenewToggleActive : ''}`}
          aria-pressed={autoRenew}
          aria-label="Автопродление"
          onClick={onToggle}
          disabled={toggleDisabled}
        >
          <span className={styles.autoRenewKnob} aria-hidden />
        </button>
      </div>
    )}
  </div>
)

const getAutoRenewErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'Нет сохранённой карты для автопродления. Сначала привяжите карту.'
  if (status === 502)
    return 'Провайдер не отвечает. Попробуйте ещё раз через минуту.'
  return 'Не удалось изменить автопродление. Попробуйте ещё раз.'
}

const getBuyErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'У вас уже есть активная подписка или незавершённый платёж.'
  if (status === 502)
    return 'Не удалось связаться с провайдером. Попробуйте ещё раз.'
  return 'Не удалось создать платёж. Попробуйте ещё раз.'
}

const SubscriptionSection = ({ onSettingsClick }: SubscriptionSectionProps) => {
  const subscription = useAppStore(s => s.subscription)
  // Грейс-период живёт только в profile.subscription (legacy-стор его не несёт).
  const graceActive = useAppStore(s => s.profile?.subscription?.grace_active === true)
  const hasActiveSubscription = useAppStore(s => s.hasActiveSubscription)
  const isActive = hasActiveSubscription()
  const setAutoRenewMutation = useSetAutoRenewMutation()
  const cancelReasonMutation = useCancelReasonMutation()
  const autoRenewError = setAutoRenewMutation.isError
    ? getAutoRenewErrorText(setAutoRenewMutation.error)
    : null
  const { lookup: planLookup } = usePlanLookup()
  const plan = planLookup(subscription.subscriptionInfoId)
  // Закреплённая цена приоритетнее тарифной: бэк фиксирует её при оформлении
  // и не меняет, даже если плановая цена поднялась. Если закреплённой нет —
  // fallback на текущую цену тарифа из справочника.
  const basePrice =
    subscription.renewalPrice != null
      ? subscription.renewalPrice
      : plan
        ? plan.price
        : null
  const baseCurrency =
    subscription.renewalCurrency ?? plan?.currency ?? 'RUB'
  // Разовая скидка на следующее списание — бэк отдаёт долю строкой ("0.15").
  // Парсим и клипуем в [0..1]; невалидное значение — без скидки.
  const parsedDiscount = (() => {
    const raw = subscription.nextChargeDiscount
    if (raw == null) return null
    const v = Number.parseFloat(raw)
    if (!Number.isFinite(v) || v <= 0) return null
    return Math.min(v, 1)
  })()
  const discountedPrice =
    basePrice != null && parsedDiscount != null
      ? Math.max(0, Math.round(basePrice * (1 - parsedDiscount)))
      : null
  const tariffSuffix = plan?.suffix ? ` / ${plan.suffix}` : ''
  const tariffRow = basePrice != null ? (
    <div className={styles.metaRow}>
      <span className={styles.metaLabel}>Тариф</span>
      <span className={styles.metaValue}>
        {discountedPrice != null && (
          <span className={styles.priceOld}>
            {formatPrice(basePrice, baseCurrency)}
          </span>
        )}
        <span>
          {formatPrice(discountedPrice ?? basePrice, baseCurrency)}
          {tariffSuffix}
        </span>
      </span>
    </div>
  ) : null
  const [isCancelOpen, setCancelOpen] = useState(false)
  // Сбор причины отмены для Tribute — отдельный поток: причина логируется,
  // затем редирект в Tribute Mini App (auto_renew наш бэк там не меняет).
  const [isTributeCancelOpen, setTributeCancelOpen] = useState(false)
  const [isResultOpen, setResultOpen] = useState(false)
  const [resultMode, setResultMode] = useState<'enabled' | 'disabled'>(
    'disabled'
  )

  const [isBuyOpen, setBuyOpen] = useState(false)
  const [buyError, setBuyError] = useState<string | null>(null)
  const { data: pricesData } = useSubscriptionPricesQuery(isBuyOpen)
  const buyMutation = useBuySubscriptionMutation()
  const startPayment = useStartPayment()
  const platform = usePlatform()
  const { data: paymentMethodsData } = usePaymentMethodsQuery()
  const paymentMethods = paymentMethodsData?.methods ?? []
  // Карта для автопродления: основная, иначе первая привязанная.
  const autoRenewCard =
    paymentMethods.find(m => m.is_main) ?? paymentMethods[0] ?? null
  // Тоггл автопродления — только для Robokassa: в Tribute управление подпиской
  // живёт в их собственном Telegram-приложении, наш PATCH туда не дотягивается.
  const isRobokassa = subscription.provider === 'robokassa'
  const isTribute = subscription.provider === 'tribute'
  const hasMainCard = isRobokassa && autoRenewCard != null

  const openTributeManage = () => {
    platform.openLink('https://t.me/tribute/app')
  }

  const openTributeCancel = () => {
    cancelReasonMutation.reset()
    setTributeCancelOpen(true)
  }
  const closeTributeCancel = () => {
    if (cancelReasonMutation.isLoading) return
    setTributeCancelOpen(false)
  }
  // Сначала логируем причину, потом редиректим в Tribute. Сбор ОС не должен
  // блокировать отмену: при ошибке логирования всё равно открываем Mini App.
  const handleTributeCancelConfirm = (reason: string | null) => {
    // Окно готовим синхронно по клику — после await mutate web блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    cancelReasonMutation.mutate(
      // Бэк требует непустой reason — «без причины» шлём как явный текст.
      { reason: reason ?? 'Без причины' },
      {
        onSettled: () => {
          setTributeCancelOpen(false)
          tab.complete('https://t.me/tribute/app')
        },
      }
    )
  }

  const handleToggleAutoRenew = () => {
    if (setAutoRenewMutation.isLoading) return
    if (subscription.autoRenew) {
      // Выключаем — открываем модалку подтверждения
      setAutoRenewMutation.reset()
      setCancelOpen(true)
    } else {
      // Включаем — бэкенду нужен payment_method_id, иначе 409 «нет карты».
      if (!autoRenewCard) return
      setAutoRenewMutation.reset()
      setAutoRenewMutation.mutate(
        { enabled: true, payment_method_id: autoRenewCard.payment_method_id },
        {
          onSuccess: () => {
            setResultMode('enabled')
            setResultOpen(true)
          },
        }
      )
    }
  }

  const openBuyModal = () => {
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(true)
  }
  const closeBuyModal = () => {
    if (buyMutation.isLoading) return
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(false)
  }
  const handleBuySubmit = ({
    planId,
    provider,
    saveCard,
    paymentMethodId,
  }: ProviderPickerSubmit) => {
    setBuyError(null)
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      {
        subscription_info_id: planId,
        provider,
        save_card: saveCard,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: invoice => {
          setBuyOpen(false)
          startPayment(invoice, tab)
        },
        onError: error => {
          tab.abort()
          setBuyError(getBuyErrorText(error))
        },
      }
    )
  }

  const closeCancelModal = () => {
    if (setAutoRenewMutation.isLoading) return
    setCancelOpen(false)
  }
  const handleConfirmCancel = (reason: string | null) => {
    setAutoRenewMutation.mutate(
      { enabled: false, reason },
      {
        onSuccess: () => {
          setCancelOpen(false)
          setResultMode('disabled')
          setResultOpen(true)
        },
      }
    )
  }

  const formattedExpiresAt = subscription.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString('ru-RU')
    : null
  // У Tribute с бонусными днями реальная дата списания (`tribute_payment_date`)
  // не совпадает с датой окончания подписки (`subscription_end_timestamp`).
  // Если поле заполнено — показываем обе даты раздельно.
  const formattedTributePaymentDate = subscription.tributePaymentDate
    ? new Date(subscription.tributePaymentDate).toLocaleDateString('ru-RU')
    : null
  const nextChargeDate = formattedTributePaymentDate ?? formattedExpiresAt
  // Бонусные дни = (subscription_end_timestamp - tribute_payment_date) в днях.
  // Считаем только если обе даты есть, иначе бонусов нет.
  const bonusDays = (() => {
    if (!subscription.tributePaymentDate || !subscription.expiresAt) return 0
    const paymentMs = new Date(subscription.tributePaymentDate).getTime()
    const endMs = new Date(subscription.expiresAt).getTime()
    if (!Number.isFinite(paymentMs) || !Number.isFinite(endMs)) return 0
    const diffDays = Math.round((endMs - paymentMs) / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  })()
  const pluralizeDays = (n: number) => {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'день'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
    return 'дней'
  }
  const bonusDaysLabel =
    bonusDays > 0
      ? `с учётом ${bonusDays} ${pluralizeDays(bonusDays)} в подарок`
      : null

  // Плашка грейс-периода: подписка скоро закончится. Показываем в обоих
  // активных состояниях; текст для Tribute — про баланс карты.
  const graceBanner = graceActive ? (
    <div className={styles.graceBanner}>
      <AlertTriangle size={18} className={styles.graceBannerIcon} aria-hidden />
      <p className={styles.graceBannerText}>
        {isTribute
          ? 'Подписка скоро закончится. Проверьте баланс карты — в течение недели мы повторим попытки продлить её.'
          : 'Подписка скоро закончится. Продлите её, чтобы не потерять доступ к контенту.'}
      </p>
    </div>
  ) : null

  // Модалки автопродления держим вне веток состояния: смена autoRenew
  // меняет ветку (early return), и если рендерить их только в одной —
  // result-модалка пропадёт сразу после успешной мутации.
  const autoRenewModals = (
    <>
      {isCancelOpen && (
        <CancelAutoRenewConfirmModal
          isSubmitting={setAutoRenewMutation.isLoading}
          errorText={autoRenewError}
          onConfirm={handleConfirmCancel}
          onClose={closeCancelModal}
        />
      )}

      {isTributeCancelOpen && (
        <CancelAutoRenewConfirmModal
          isSubmitting={cancelReasonMutation.isLoading}
          onConfirm={handleTributeCancelConfirm}
          onClose={closeTributeCancel}
        />
      )}

      {isResultOpen && (
        <AutoRenewDisabledResultModal
          expiresAtFormatted={formattedExpiresAt}
          mode={resultMode}
          onClose={() => setResultOpen(false)}
        />
      )}
    </>
  )

  // Состояние «Подписка неактивна»: либо нет подписки, либо `is_active=false`,
  // либо срок уже прошёл (вычисляется в `hasActiveSubscription`).
  if (!isActive) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          <p className={styles.statusText}>Подписка неактивна</p>
          <p className={styles.description}>
            Оформите подписку, чтобы получить доступ ко всему контенту.
          </p>
        </div>
        <PaymentMethodsBlock />

        <Button onClick={openBuyModal} block>
          Оформить подписку
        </Button>
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />

        {isBuyOpen && (
          <ProviderPickerModal
            plans={pricesData?.subscriptions ?? []}
            override={pricesData?.override}
            paymentMethods={paymentMethods}
            onSubmit={handleBuySubmit}
            onClose={closeBuyModal}
            isSubmitting={buyMutation.isLoading}
            errorText={buyError}
          />
        )}
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление отключено».
  // Подписка работает до `expiresAt`, но дальше не продлится — нужна кнопка «Возобновить».
  if (subscription.autoRenew === false) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          {graceBanner}
          <ActiveDateRow
            formattedExpiresAt={formattedExpiresAt}
            autoRenew={false}
            showToggle={hasMainCard}
            toggleDisabled={setAutoRenewMutation.isLoading}
            onToggle={handleToggleAutoRenew}
            bonusDaysLabel={bonusDaysLabel}
          />
          {/* У Tribute с бонусными днями дата реального списания приходит отдельно
              и не равна дате окончания подписки — показываем её здесь. */}
          {formattedTributePaymentDate && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Следующее списание</span>
              <span className={styles.metaValue}>
                {formattedTributePaymentDate}
              </span>
            </div>
          )}
          {tariffRow}
          {!isTribute && (
            <p className={styles.description}>
              Автопродление отключено. После окончания срока доступ к подписке прекратится.
            </p>
          )}
          {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
        </div>
        <PaymentMethodsBlock />
        {isTribute && (
          <>
            <button className="support-button" onClick={openTributeManage}>
              Управлять подпиской
            </button>
            <button
              className={`support-button ${styles.cancelButton}`}
              onClick={openTributeCancel}
            >
              Отмена подписки
            </button>
          </>
        )}
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление включено».
  return (
    <>
      <div className={`profile-section ${styles.section}`}>
        <h2 className="section-title">Подписка</h2>

        {graceBanner}

        <ActiveDateRow
          formattedExpiresAt={formattedExpiresAt}
          autoRenew={true}
          showToggle={hasMainCard}
          toggleDisabled={setAutoRenewMutation.isLoading}
          onToggle={handleToggleAutoRenew}
        />

        {tariffRow}

        {nextChargeDate && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Следующее списание</span>
            <span className={styles.metaValue}>
              {discountedPrice != null && basePrice != null ? (
                <>
                  <span className={styles.priceOld}>
                    {formatPrice(basePrice, baseCurrency)}
                  </span>
                  <span>{formatPrice(discountedPrice, baseCurrency)}</span>
                  <span className={styles.priceDate}> · {nextChargeDate}</span>
                </>
              ) : (
                nextChargeDate
              )}
            </span>
          </div>
        )}

        {formattedTributePaymentDate && formattedExpiresAt && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Подписка активна до</span>
            <span className={styles.metaValue}>
              {formattedExpiresAt}
              {bonusDaysLabel && (
                <span className={styles.bonusDays}> · {bonusDaysLabel}</span>
              )}
            </span>
          </div>
        )}

        {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
      </div>
      <PaymentMethodsBlock />

      {isTribute && (
        <>
          <button className="support-button" onClick={openTributeManage}>
            Управлять подпиской
          </button>
          <button
            className={`support-button ${styles.cancelButton}`}
            onClick={openTributeCancel}
          >
            Отмена подписки
          </button>
        </>
      )}
      <button className="support-button" onClick={onSettingsClick}>
        Тех. поддержка
      </button>
      <RecommendedChannels defaultOpen={true} />
      {autoRenewModals}
    </>
  )
}

export default SubscriptionSection
